import React, { createContext, useContext, useState, useEffect } from "react";
import { AuthContextState } from "../types";
import XanoAPI from "./XanoAPI";

interface AuthContextProviderProps {
  children: React.ReactNode;
}

const initialContextState: AuthContextState = {
  isLoading: true,
  isAuthenticated: false,
  hasTOTPWarning: false,
  currentUser: null,
  token: "",
  clients: {},
  roles: [],
  featureFlags: [], // Add featureFlags to the initial state
};

export const AuthContext = createContext<AuthContextState>(initialContextState);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<AuthContextProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<AuthContextState>(initialContextState);
  const api = XanoAPI.getInstance(); // Prevent re-instantiation

  const checkAuth = async () => {
    try {
      const token = api.getToken();
      const isAuthenticated = api.isAuthenticated();
      const currentUser = isAuthenticated ? await api.getCurrentUser() : null;

      const clients = isAuthenticated ? await api.getAllClients() : {};
      const userAgency = currentUser?.user_agency ?? [];

      const hasTOTPWarning =
        !!userAgency.length &&
        userAgency[0]?.totp_required &&
        !currentUser?.totp_enabled;

      const roles =
        isAuthenticated && currentUser?.agency_id
          ? await api.getRoles(currentUser.agency_id)
          : [];

      // Always fetch fresh feature flags
      let featureFlags = await api.getFeatureFlags();

      // If user is admin, add admin-specific feature flags
      if (
        isAuthenticated &&
        currentUser?.user_permissions?.[0]?.role === "admin"
      ) {
        const adminFeatureFlags = [
          "calendar",
          "performance",
          "sales",
          "commission_tracking",
          "knowledge_base",
          "2fa",
          "api_keys",
          "team",
          "reports",
          "billing",
          "client_lock",
        ];

        // Extract existing flag names
        const existingFlagNames = featureFlags.map((flag) => flag.name);

        // Add missing flags for admin users
        const missingFlags = adminFeatureFlags.filter(
          (flagName) => !existingFlagNames.includes(flagName)
        );

        if (missingFlags.length > 0) {
          // Create virtual flags for admin users
          const virtualFlags = missingFlags.map((flagName) => ({
            id: `admin_${flagName}`,
            name: flagName,
            dev: true,
            production: true,
            created_at: new Date().toISOString(),
          }));

          featureFlags = [...featureFlags, ...virtualFlags];
        }
      }

      if (currentUser?.id && currentUser.id !== api.getUserId()) {
        api.setUserId(currentUser.id);
      }

      const userState = {
        token,
        isLoading: false,
        isAuthenticated,
        hasTOTPWarning,
        currentUser,
        clients,
        roles,
        featureFlags, // Use fresh feature flags
      };

      setState(userState);

      // DEBUG: Log user state to understand permission structure
      console.log("=== AUTH CONTEXT DEBUG ===");
      console.log("currentUser from API:", currentUser);
      console.log(
        "currentUser.user_permissions:",
        currentUser?.user_permissions
      );
      console.log("currentUser.user_agency:", currentUser?.user_agency);
      console.log("currentUser.roles:", currentUser?.roles);
      console.log("roles from getRoles:", roles);
      console.log("=== END AUTH CONTEXT DEBUG ===");
    } catch (error) {
      setState({ ...initialContextState, isLoading: false });
      console.error("Error in checkAuth:", error);
    }
  };

  useEffect(() => {
    setState((prevState) => ({ ...prevState, isLoading: true }));
    checkAuth();
  }, [api]);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};
